import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
  ColDef,
  ColumnApi,
  GridApi,
  GridReadyEvent,
  IServerSideGetRowsParams,
  RowClassRules,
  ValueFormatterParams
} from 'ag-grid-community';
import {Observable} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {HttpService} from '../../../services/http.service';
import {StorageService} from '../../../services/storage.service';
import {renderUpdateMethod} from '../../../helpers/update-method.util';
import {
  CellHistoryRestoreButtonComponent
} from '../../cell-editors/cell-history-restore-button/cell-history-restore-button.component';

@Component({
  selector: 'app-history-dialog',
  templateUrl: './history-dialog.component.html',
  styleUrls: ['./history-dialog.component.scss']
})
export class HistoryDialogComponent implements OnInit {
  public gridApi?: GridApi;
  public columnApi?: ColumnApi;

  public columnName: string;
  public sku: string;
  public product_id: number;
  public field: string;
  public rowData: any = [];

  public readonly paginationPageSize = 10000;
  public readonly cacheBlockSize = 33;
  public readonly maxBlocksInCache = 20;
  public readonly maxConcurrentDatasourceRequests = 2;
  public rowModelType: any = 'serverSide';

  public defaultColDef: ColDef = {};
  public rowClassRules: RowClassRules = {}
  public columnDefs: any = [];
  public readonly sideBar = {};
  public readonly rowSelection = 'multiple';

  constructor(
    private dialogRef: MatDialogRef<HistoryDialogComponent>,
    private httpService: HttpService,
    private storageService: StorageService,
    @Inject(MAT_DIALOG_DATA) data: any,
  ) {
    console.log('data', data);
    this.columnName = data.column;
    this.sku = data.sku;
    this.field = data.field;
    this.product_id = data.product_id;
    this.defaultColDef = {
      resizable: true,
      flex: 1,
      minWidth: 120,
      sortable: data.clientSideData,
      editable: false,
      enableRowGroup: false,
      suppressColumnsToolPanel: false
    };

    this.columnDefs = [
      {
        field: '_id',
        hide: true,
        suppressFiltersToolPanel: true,
        suppressColumnsToolPanel: true,
      },
      {
        field: 'user._id',
        headerName: 'User',
        maxWidth: 220,
        valueFormatter: (params: ValueFormatterParams) => {
          const user = this.storageService.users.find((u: any) => u._id === params.value);
          return user.name;
        }
      },
      {
        field: 'created_at',
        headerName: 'Date',
        minWidth: 200,
        maxWidth: 220,
        sort: 'desc',
        valueFormatter: (data: any) => {
          return data.value ? (new Date(data.value)).toLocaleDateString() + ' ' + (new Date(data.value)).toLocaleTimeString() : '';
        }
      },
      {
        field: 'previous_value',
        headerName: this.columnName,
        minWidth: 310,
        cellRenderer: (params: ValueFormatterParams) => {
          // console.log('🟣', params);
          if (['list_price', 'sell_price', 'cost_price'].includes(this.field)) {
            return '$' + parseFloat(params.value).toFixed(2).toString();
          } else if (this.field === 'brand') {
            return this.storageService.brands.find((b: any) => b._id === params.value._id).name;
          } else if (this.field === 'shipping_tag') {
            return params.value.name;
          } else if (this.field === 'type') {
            if (this.storageService.types.find((t: any) => t._id === params.value._id)) {
              return this.storageService.types.find((t: any) => t._id === params.value._id).name;
            } else {
              return 'Not Obsolete';
            }
          } else if (this.field === 'tags') {
            return params.value.map((tag: any) => tag.name).join(', ');
          } else if (this.field === 'datasheet_new') {
            return params.value.map((x: any) => x.url).join(', ');
          } else if (this.field === 'replacement') {
            if (params.value.obsolete === false) {
              return 'false';
            } else if (params.value.obsolete === true && params.value.sku === undefined) {
              return 'true';
            } else {
              return params.value.sku;
            }
          } else if (this.field === 'vendor_tags') {
            return params.value.map((product: any) => product.name).join(', ');
          } else if (this.field === 'related_products' || this.field === 'related_parts') {
            return params.value.map((product: any) => product.sku).join(', ');
          } else if (this.field === 'collections') {
            return '<div class="collection-tag__container">' + params.value.map((collection: any) => `<span class="collection-tag">${collection.title.split(' -> ').join(' → ')}</span>`).join('') + '</div>';
          } else if (String(params.value) === 'false') {
            return 'false';
          } else {
            return params.value || null;
          }
        }
      },
      {
        field: 'notes',
        headerName: 'Update method',
        minWidth: 100,
        // maxWidth: 100,
        cellRenderer: (params: ValueFormatterParams) => {
          // Drop the Import link when its uploaded file is past retention.
          const html = renderUpdateMethod(params.value);
          if (html.includes('<a')) {
            const a = document.createElement('div');
            a.innerHTML = html;
            return a;
          } else {
            return html;
          }
        }
      },
      {
        field: 'action',
        maxWidth: 120,
        headerName: 'Restore',
        pinned: 'right',
        disabled: true,
        suppressFiltersToolPanel: true,
        suppressColumnsToolPanel: true,
        hide: data.disableRollBack,
        cellClass: ['history-dialog-action-cell'],
        cellRenderer: CellHistoryRestoreButtonComponent
      }
    ];

    if (data.clientSideData) {
      this.rowModelType = 'clientSide';
      this.rowData = data.params.node.data.logs[this.field];
    }
  }

  ngOnInit(): void {}

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;

    const that = this;
    this.gridApi.setServerSideDatasource({
      getRows(params: IServerSideGetRowsParams) {
        const {startRow, endRow, rowGroupCols, groupKeys, filterModel, sortModel} = params.request;

        const body = {
          product_id: that.product_id,
          field: that.field,
          startRow, endRow, rowGroupCols, groupKeys, filterModel, sortModel};
        console.log('body', body);
        that.httpService.post(environment.API_PATH + '/product/field/changes', body).subscribe((dataAndCount) => {
          const receivedData = dataAndCount as { rows: {}[], count: number };
          params.success({
            rowData: receivedData.rows,
            rowCount: receivedData.count
          });
        });
      }
    });
  }
}
